import { useRouter } from 'next/router'
import QueryString from 'qs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { ActionButtonVariant } from 'src/components/ActionButton/styled'
import AdsFormAlertModal from 'src/components/AdsFormAlertModal'
import { AdsSortingFiltersVariant } from 'src/components/AdsSortingFilters/styled'
import { Icons } from 'src/components/Icon'
import { humanDate } from 'src/helpers/DateHelpers'
import { useRedirection } from 'src/hooks/useRedirection'
import { actions, selectors } from 'src/redux'
import { RedirectionType } from 'src/redux/redirections/redux'
import useFooter from 'src/relay/Footer/useFooter'
import useUpdateAlert from 'src/relay/Modals/useUpdateAlert'
import { ClassifiedEmitterFiltersTransformer } from 'src/relay/Transformers/ClassifiedEmitterFiltersTransformer'
import useHeaderBis from 'src/relay/header/useHeaderBis'
import {
  Classified,
  ClassifiedCategories,
  ClassifiedSpecialties,
} from 'src/rest/types/Classified'
import { Configuration } from 'src/rest/types/Configuration'
import { PageType } from 'src/rest/types/Page'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import { SearchTemplateProps } from 'src/templates/SearchTemplate'
import tracking from 'src/tracking'
import { ListDisplayType } from 'src/types/classified'
import { useAdsSortingFiltersFormProps } from './Forms/useAdsSortingFiltersFormProps'
import useAdsListDisplayTypes from './useAdsListDisplayTypes'
import { useBannerTitleTextButtonAlertProps } from './useBannerTitleTextButtonAlertProps'

export const useSearchPageProps = ({
  categories,
  configuration,
  filters,
  specialties,
  classifieds,
  numberClassifieds,
  pagination,
  displayTypeParams,
  isError,
}: {
  configuration: Configuration
  classifieds: Array<Classified>
  numberClassifieds: number
  specialties: ClassifiedSpecialties
  categories: ClassifiedCategories
  filters: {
    specialities: Array<string> | string | null
    offerType: Array<string> | string | null
    localization: string | null
    terms: string | null
    radius: string
    lat: string
    lng: string
    startDate: string
    endDate: string
    days: string[]
  }
  pagination: {
    currentPage: number
    nbPages: number
  }
  displayTypeParams: ListDisplayType
  isError?: boolean
}): SearchTemplateProps => {
  const { t } = useTranslation()
  const headerProps = useHeaderBis(configuration, PageType.Classifieds)
  const footerProps = useFooter(configuration)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const router = useRouter()
  const isLogin = useSelector(selectors.auth.isConnected)
  const modalAlertProps = useUpdateAlert({ specialties })
  const [displayType, setDisplayType] = useState<ListDisplayType>(
    displayTypeParams ?? ListDisplayType.Card
  )
  const dispatch = useDispatch()
  const { asPath } = useRouter()
  const { hasRedirected, redirectType, setRedirection, resetRedirection } =
    useRedirection()
  const isUserLoading = useSelector(selectors.auth.meIsLoading)
  const user = useSelector(selectors.auth.user)
  const hasTrackedRef = useRef<boolean>(false)

  const listProps = useAdsListDisplayTypes({
    classifieds,
    displayTypeParams,
  })

  const openAlertModal = useCallback(() => {
    dispatch(actions.modals.setUpdateAlertModalIsOpen(true))
    dispatch(
      actions.user.setUpdatedAlert({
        specialities: Array.isArray(filters.specialities)
          ? filters.specialities.map((speciality) => ({
              id: Number(speciality),
            }))
          : filters.specialities !== null
          ? [{ id: Number(filters.specialities) }]
          : [],
        categories: Array.isArray(filters.offerType)
          ? filters.offerType.map((offerTypeItem) => ({
              id: Number(offerTypeItem),
            }))
          : filters.offerType !== null
          ? [{ id: Number(filters.offerType) }]
          : [],
        localisation: filters?.localization,
        terms: filters?.terms,
      })
    )
  }, [dispatch, filters])

  useEffect(() => {
    if (hasRedirected && redirectType === RedirectionType.Alert) {
      openAlertModal()
      resetRedirection()
    }
  }, [hasRedirected, redirectType, openAlertModal, resetRedirection])

  useEffect(() => {
    if (displayType !== displayTypeParams) {
      Router.push(
        Router.getRouteUrl(routes.classifiedSearch) +
          '?' +
          QueryString.stringify(
            {
              ...(filters.offerType && {
                offerType: filters.offerType,
              }),
              ...(filters.terms && {
                terms: filters.terms,
              }),
              ...(filters.lat && {
                lat: filters.lat,
              }),
              ...(filters.lng && {
                lng: filters.lng,
              }),
              ...(filters.radius && {
                radius: filters.radius,
              }),
              ...(filters.localization && {
                localization: filters.localization,
              }),
              ...(filters.specialities && {
                specialities: filters.specialities,
              }),
              ...(filters.startDate && {
                startDate: filters.startDate,
              }),
              ...(filters.endDate && {
                endDate: filters.endDate,
              }),
              ...(filters.days && {
                days: filters.days,
              }),
              displayType: displayType,
            },
            { arrayFormat: 'comma' }
          )
      )
    }
  }, [
    displayTypeParams,
    filters.lat,
    filters.lng,
    filters.offerType,
    filters.specialities,
    filters.terms,
    displayType,
    filters.radius,
    filters.localization,
    filters.startDate,
    filters.endDate,
    filters.days,
  ])

  useEffect(() => {
    setIsLoading(false)
  }, [router.asPath])

  const searchedCategories = categories.filter((c) =>
    Array.isArray(filters.offerType)
      ? filters.offerType.includes(String(c.id))
      : filters.offerType === String(c.id)
  )

  const searchedSpecialties = specialties.filter((c) =>
    Array.isArray(filters.specialities)
      ? filters.specialities.includes(String(c.id))
      : filters.specialities === String(c.id)
  )

  useEffect(() => {
    if (!isUserLoading) {
      const selectedCategories = searchedCategories.map((cat) => cat.name)
      const selectedSpecialties = searchedSpecialties.map((cat) => cat.name)

      tracking.search_pa(
        filters.terms ?? '',
        filters.localization ?? '',
        selectedCategories,
        selectedSpecialties,
        numberClassifieds,
        user?.id,
        user?.sessionId
      )
    }
  }, [
    filters,
    isUserLoading,
    numberClassifieds,
    searchedCategories,
    searchedSpecialties,
    user,
  ])

  const formProps = useAdsSortingFiltersFormProps({
    categories,
    specialities: specialties,
    isOpen,
    isLoading,
    initialValues: {
      offerType: filters.offerType
        ? Array.isArray(filters.offerType)
          ? filters.offerType
          : [filters.offerType]
        : [],
      specialities: filters.specialities
        ? Array.isArray(filters.specialities)
          ? filters.specialities
          : [filters.specialities]
        : [],
      localization: filters.localization ?? '',
      radius: filters.radius.length > 0 ? filters.radius : '2000',
      term: filters.terms ?? '',
      lat: filters.lat,
      lng: filters.lng,
      startDate: filters.startDate ?? '',
      endDate: filters.endDate ?? '',
      days: filters.days ?? [],
    },
    onSubmit: (values) => {
      setIsLoading(true)
      hasTrackedRef.current = false
      Router.push(
        Router.getRouteUrl(routes.classifiedSearch) +
          '?' +
          QueryString.stringify(
            {
              ...(values.offerType && {
                offerType: values.offerType,
              }),
              ...(values.term && {
                terms: values.term,
              }),
              ...(values.localization &&
                values.lat && {
                  lat: values?.lat,
                }),
              ...(values.localization &&
                values.lng && {
                  lng: values?.lng,
                }),
              ...(values.localization &&
                values.radius && {
                  radius: values.radius,
                }),
              ...(values.localization && {
                localization: values.localization,
              }),
              ...(values.specialities && {
                specialities: values.specialities,
              }),
              ...(displayType && {
                displayType: displayType,
              }),
              ...(values.startDate && {
                startDate: values.startDate,
              }),
              ...(values.endDate && {
                endDate: values.endDate,
              }),
              ...(values.days && {
                days: values.days,
              }),
            },
            { arrayFormat: 'comma' }
          )
      )
    },
    submitButtonVariant: 'secondary',
  })

  return {
    sortingFilters: {
      ...ClassifiedEmitterFiltersTransformer({
        t,
        classifieds,
        numberClassifieds,
        setDisplayType,
        displayTypeParams,
      }),
      title: t('listingSearchAdsBannerTitle'),
      searchForm: formProps,
      searchLabel: t('listingSearchAdsSearchLabel'),
      termValue: filters?.terms ?? '',
      searchFieldValue: [
        ...searchedSpecialties.map((specialty) => ({
          value: specialty.name,
          icon: {
            icon: Icons.profil,
            height: 24,
            width: 24,
          },
        })),
        ...searchedCategories.map((category) => ({
          value: category.name ?? '',
          icon: {
            icon: Icons.stethoscopeSecondary,
            height: 24,
            width: 24,
          },
        })),
        ...(filters.localization
          ? [
              {
                value: filters.localization,
                icon: {
                  icon: Icons.markerMap,
                  height: 24,
                  width: 24,
                },
              },
            ]
          : []),
        ...(filters.days
          ? [
              {
                value: Array.isArray(filters.days)
                  ? filters.days
                      .map((day) =>
                        t(`form_search_ads_days_option_${day}` as any)
                      )
                      .join(', ')
                  : t(`form_search_ads_days_option_${filters.days}` as any),
                icon: {
                  icon: Icons.schedule,
                  height: 24,
                  width: 24,
                },
              },
            ]
          : []),
        ...(filters.startDate && filters.endDate
          ? [
              {
                value: `${humanDate(filters.startDate)} - ${humanDate(
                  filters.endDate
                )}`,
                icon: {
                  icon: Icons.schedule,
                  height: 24,
                  width: 24,
                },
              },
            ]
          : []),
      ],
      alertCreationButton: {
        ...(isLogin && {
          onClick: openAlertModal,
        }),
        ...(!isLogin && {
          onClick: () => {
            setRedirection(RedirectionType.Alert, asPath)
            Router.push(Router.getRouteUrl(routes.loginOrRegister))
          },
        }),
        text: t('banner_title_text_button_alert_button_text'),
        variant: ActionButtonVariant.quaternary,
      },
      filterButton: {
        text: t('listingSearchAdsFilterButton'),
        onClick: () => setIsOpen(!isOpen),
      },
      variant: AdsSortingFiltersVariant.secondary,
      isOpen,
    },
    ...listProps,
    ...(pagination.nbPages > 1 &&
      displayTypeParams !== ListDisplayType.Map && {
        pagination: {
          items: Array.from(Array(pagination.nbPages).keys()).map((_, idx) => {
            const value = idx + 1
            return {
              text: String(value),
              current: value === pagination.currentPage,
              href:
                Router.getRouteUrl(routes.classifiedSearch) +
                '?' +
                QueryString.stringify(
                  {
                    ...(filters.offerType && {
                      offerType: filters.offerType,
                    }),
                    ...(filters.terms && {
                      terms: filters.terms,
                    }),
                    ...(filters.lat && {
                      lat: filters.lat,
                    }),
                    ...(filters.lng && {
                      lng: filters.lng,
                    }),
                    ...(filters.radius && {
                      radius: filters.radius,
                    }),
                    ...(filters.localization && {
                      localization: filters.localization,
                    }),
                    ...(filters.specialities && {
                      specialities: filters.specialities,
                    }),
                    displayType: displayType,
                    page: value,
                  },
                  { arrayFormat: 'comma' }
                ),
            }
          }),
        },
      }),
    infoBanner: useBannerTitleTextButtonAlertProps({
      t,
      isLogin,
      openAlertModal,
    }),
    slotModals: <AdsFormAlertModal {...modalAlertProps} />,
    error: isError ? t('classified_search_algolia_error') : undefined,
    headerProps,
    footerProps,
  }
}
